import { mapValues } from "lodash-es";

/**
 * UNITS has a hash of all unit classes,
 * such that each unit class contains a hash of unit nad its value in that class
 *
 * To assign units, use '<CALSS>.<UNIT>' format. For Example
 * 'STORAGE.MB', 'TIME.s', 'COUNT._', 'PERCENT._', '_STORAGE_PER_TIME.KB/s'
 */
const UNITS = {
	STORAGE: {
		DEFAULT: { BEFORE_NUMBER: false },
		UNITS: {
			b: { NAME: "Bit", ABBRIVIATION: "b", VALUE: 1 },
			Kb: { NAME: "Kilobit", ABBRIVIATION: "Kb", VALUE: 1024 },
			Mb: { NAME: "Megabit", ABBRIVIATION: "Mb", VALUE: 1024 * 1024 },
			Gb: { NAME: "Gigabit", ABBRIVIATION: "Gb", VALUE: 1024 * 1024 * 1024 },
			Tb: { NAME: "Terabit", ABBRIVIATION: "Tb", VALUE: 1024 * 1024 * 1024 * 1024 },
			B: { NAME: "Byte", ABBRIVIATION: "B", VALUE: 8 },
			KB: { NAME: "Kilobyte", ABBRIVIATION: "KB", VALUE: 8 * 1024 },
			MB: { NAME: "Megabyte", ABBRIVIATION: "MB", VALUE: 8 * 1024 * 1024 },
			GB: { NAME: "Gigabyte", ABBRIVIATION: "GB", VALUE: 8 * 1024 * 1024 * 1024 },
			TB: { NAME: "Terabyte", ABBRIVIATION: "TB", VALUE: 8 * 1024 * 1024 * 1024 * 1024 }
		}
	},
	TIME: {
		DEFAULT: { BEFORE_NUMBER: false },
		UNITS: {
			μs: { NAME: "Microsecond", ABBRIVIATION: "μs", VALUE: 1 },
			ms: { NAME: "Millisecond", ABBRIVIATION: "ms", VALUE: 1000 },
			s: { NAME: "Second", ABBRIVIATION: "s", VALUE: 1000 * 1000 },
			min: { NAME: "Minute", ABBRIVIATION: "min", VALUE: 1000 * 1000 * 60 },
			hr: { NAME: "Hour", ABBRIVIATION: "hr", VALUE: 1000 * 1000 * 60 * 60 },
			d: { NAME: "Day", ABBRIVIATION: "d", VALUE: 1000 * 1000 * 60 * 60 * 24 }
		}
	},
	CURRENCY: {
		DEFAULT: { BEFORE_NUMBER: true },
		UNITS: {
			$: { NAME: "USD", ABBRIVIATION: "$", VALUE: 1 }
		}
	},
	COUNT: {
		DEFAULT: { BEFORE_NUMBER: false },
		UNITS: {
			_: { NAME: "Number", ABBRIVIATION: "#", VALUE: 1 },
			k: { NAME: "Thousand", ABBRIVIATION: "k", VALUE: 1000 },
			m: { NAME: "Million", ABBRIVIATION: "m", VALUE: 1000 * 1000 }
		}
	},
	PERCENT: {
		DEFAULT: { BEFORE_NUMBER: false },
		UNITS: {
			"%": { NAME: "Percent", ABBRIVIATION: "%", VALUE: 1 }
		}
	},
	STORAGE_PER_TIME: {
		DEFAULT: { BEFORE_NUMBER: false }
	},
	COUNT_PER_TIME: {
		DEFAULT: { BEFORE_NUMBER: false }
	}
};

UNITS.STORAGE_PER_TIME.UNITS = populateDerivedUnits(
	"u1/u2",
	"u1 per u2",
	UNITS.STORAGE.UNITS,
	UNITS.TIME.UNITS
);
UNITS.COUNT_PER_TIME.UNITS = populateDerivedUnits(
	"u1/u2",
	"u1 per u2",
	UNITS.COUNT.UNITS,
	UNITS.TIME.UNITS
);

const UNIT_VAL = mapValues(UNITS, meta => {
	return mapValues(meta.UNITS, "VALUE");
});

function populateDerivedUnits(equation, naming, class1, class2) {
	const result = {};

	// A helper function to calculate the unit value
	const calculateValue = (subEquation, v1, v2) => {
		// Split the equation at each '/'
		const parts = subEquation.split("/");
		// Assume equation is always in the format 'u1/u2'
		return (parts[0] === "u1" ? v1 : v2) / (parts[1] === "u2" ? v2 : v1);
	};

	Object.keys(class1).forEach(u1 => {
		Object.keys(class2).forEach(u2 => {
			const unit = equation.replace(/\bu1\b/g, u1).replace(/\bu2\b/g, u2);
			const v1 = class1[u1],
				v2 = class2[u2];

			const name = naming
				.replace(/\bu1\b/g, v1.NAME.toLowerCase())
				.replace(/\bU1\b/g, v1.NAME)
				.replace(/\bu2\b/g, v2.NAME.toLowerCase())
				.replace(/\bU2\b/g, v2.NAME);

			const abbreviation = equation
				.replace(/\bu1\b/g, v1.ABBRIVIATION)
				.replace(/\bu2\b/g, v2.ABBRIVIATION);

			result[unit] = {
				NAME: name,
				ABBRIVIATION: abbreviation,
				VALUE: calculateValue(equation, v1.VALUE, v2.VALUE)
			};
		});
	});

	return result;
}

export default { UNITS, UNIT_VAL };
export { UNITS, UNIT_VAL };
